import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { CONFIG } from "../config";

const CompliancesGraph = ({ selectedDate, selectedStatus, selectedAgeRange, selectedCompliance, onSelectedComplianceChange }) => {
  const containerRef = useRef();
  const svgRef = useRef();
  const rowHeight = 85;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const restrictedIds = JSON.parse(process.env.REACT_APP_RESTRICTED_USER_ID || '[]');
  const userId = parseInt(localStorage.getItem("tensor-user-id"), 10);
  const isRestricted = restrictedIds.includes(userId);
  const validCompliances = ["Inspection Compliance (Non Compliant)", "Inspection Compliance (Compliant)", "Maintenance Compliance (Non Compliant)", "Maintenance Compliance (Compliant)", "DVIR Compliance (Compliant)", "DVIR Compliance (Non Compliant)"];

  const isValidCompliance = validCompliances.includes(selectedCompliance);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      const { width } = entry.contentRect;
      setDimensions(prev => {
        if (prev.width !== width) {
          return { ...prev, width };
        }
        return prev;
      });
    });

    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!selectedDate) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${CONFIG.API_BASE_URL}/${CONFIG.COMPLIANCES_GRAPH_URL}/${localStorage.getItem(
          "tensor-user-id"
        )}?date_str=${selectedDate}&selected_status=${selectedStatus}&selected_agerange=${selectedAgeRange}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("tensor-auth-token"),
          },
        });

        const result = await response.json();
        if (result.success && Array.isArray(result.data)) {
          setData(result.data);
        } else {
          setData([]);
        }
      } catch (error) {
        console.error("Failed to compliances data:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedDate, selectedStatus, selectedAgeRange]);

  useEffect(() => {
    if (!data.length || !dimensions.width) return;

    const drawChart = () => {
      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();

      const fullWidth = svgRef.current?.parentElement?.offsetWidth || 500;
      const margin = { top: 30, right: 50, bottom: 10, left: 260 };
      const height = margin.top + data.length * rowHeight + margin.bottom;
      const barWidth = fullWidth - margin.left - margin.right;
      const dynamicHeight = margin.top + data.length * rowHeight + margin.bottom;

      svg
        .attr("width", fullWidth)
        .attr("height", dynamicHeight)
        .attr("viewBox", `0 0 ${fullWidth} ${dynamicHeight}`)
        .attr("preserveAspectRatio", "xMinYMin meet");

      const maxValue = d3.max(data, d => d.compliant + d.non_compliant);
      const xScale = d3.scaleLinear().domain([0, maxValue]).range([0, barWidth]);
      const yScale = d3.scaleBand()
        .domain(data.map(d => d.compliance_name))
        .range([margin.top, height - margin.bottom])
        .padding(0.6);

      const g = svg.append("g").attr("transform", `translate(${margin.left}, 0)`);

      svg.selectAll(".issue-label")
        .data(data)
        .enter()
        .append("text")
        .attr("x", 5)
        .attr("y", d => yScale(d.compliance_name) + yScale.bandwidth() / 2)
        .style("font-family", "Noto Sans")
        .style("font-size", "16px")
        .style("font-style", "normal")
        .style("font-weight", 400)
        .style("fill", "#1C2024")
        .attr("alignment-baseline", "middle")
        .text((d, i) => `${d.compliance_name}`);

      // Compliant bar
      g.selectAll(".bar-last6")
        .data(data)
        .enter()
        .append("rect")
        .attr("x", 0)
        .attr("y", d => yScale(d.compliance_name))
        .attr("width", d => xScale(d.compliant))
        .attr("height", yScale.bandwidth())
        .attr("fill", "#30A46C")
        .style("cursor", "pointer")
        .style("opacity", d => {
          const key = `${d.compliance_name} (Compliant)`;
          if (!isValidCompliance) return 1;
          return selectedCompliance === key ? 1 : 0.4;
        })
        .on("click", (event, d) => {
          if (onSelectedComplianceChange) {
            onSelectedComplianceChange(`${d.compliance_name} (Compliant)`);
          }
        });

      // Non Compliant bar
      g.selectAll(".bar-next4")
        .data(data)
        .enter()
        .append("rect")
        .attr("x", d => xScale(d.compliant))
        .attr("y", d => yScale(d.compliance_name))
        .attr("width", d => xScale(d.non_compliant))
        .attr("height", yScale.bandwidth())
        .attr("fill", "#E5484D")
        .style("cursor", "pointer")
        .style("opacity", d => {
          const key = `${d.compliance_name} (Non Compliant)`;
          if (!isValidCompliance) return 1;
          return selectedCompliance === key ? 1 : 0.4;
        })
        .on("click", (event, d) => {
          if (onSelectedComplianceChange) {
            onSelectedComplianceChange(`${d.compliance_name} (Non Compliant)`);
          }
        });

      g.selectAll(".text-last6")
        .data(data)
        .enter()
        .append("text")
        .attr("x", -35)
        .attr("y", d => yScale(d.compliance_name) + yScale.bandwidth() / 2 + 4)
        .attr("fill", "#30A46C")
        .style("font-family", "Noto Sans")
        .style("font-size", "16px")
        .style("font-style", "normal")
        .attr("font-weight", "700")
        .text(d => `${d.compliant}%`);

      g.selectAll(".text-next4")
        .data(data)
        .enter()
        .append("text")
        .attr("x", d => xScale(d.compliant + d.non_compliant) + 5)
        .attr("y", d => yScale(d.compliance_name) + yScale.bandwidth() / 2 + 4)
        .attr("fill", "#E5484D")
        .style("font-family", "Noto Sans")
        .style("font-size", "16px")
        .style("font-style", "normal")
        .attr("font-weight", "700")
        .text(d => `${d.non_compliant}%`);

      const legend = svg.append("g").attr("transform", `translate(10, ${margin.top})`);
      legend.append("rect").attr("x", -6).attr("y", -26).attr("width", 12).attr("height", 12).attr("fill", "#30A46C");
      legend.append("text").attr("x", 12).attr("y", -16).text("Compliant").style("font-size", "12px");
      legend.append("rect").attr("x", 94).attr("y", -26).attr("width", 12).attr("height", 12).attr("fill", "#E5484D");
      legend.append("text").attr("x", 110).attr("y", -16).text("Non-Compliant").style("font-size", "12px");
    };

    drawChart();
    window.addEventListener("resize", drawChart);
    return () => window.removeEventListener("resize", drawChart);
  }, [data, dimensions, selectedCompliance]);

return (
  <div ref={containerRef}>
    <h2 className="text-xl font-semibold mb-4 text-2xl font-bold truck_page_head_text">
      Compliances
    </h2>

    {loading ? (
      <div className="flex justify-center items-center min-h-[180px]">
        <svg className="w-6 h-6 text-blue-500 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"></circle>
          <path className="opacity-75" fill="currentColor"
            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z">
          </path>
        </svg>
      </div>
    ) : isRestricted ? (
      <div className="pt-4 pr-4 pl-4 pb-0 bg-white">
        <div className="flex justify-center items-center min-h-[295px]">
          <p className="text-gray-400 text-sm">No Data Available</p>
        </div>
      </div>
    ) : (
      <div className="pt-4 pr-4 pl-4 pb-0 bg-white">
        <div className="w-full overflow-x-auto">
          <svg ref={svgRef}></svg>
        </div>
      </div>
    )}
  </div>
);

};

export default CompliancesGraph;